import { worldLeftX, worldCenterX } from "../system/worldCoords.js";
import { onReset } from "../system/teardown.js";
import { transport, setSpeedMultiplier } from "../system/transportState.js";
// speed.js

/**
 * ============================================================================
 * SPEED CUES — POSITIONAL (SVG traversal)
 * ============================================================================
 * speed(x) cues embedded in SVG are POSITION-BASED and apply INSTANTLY.
 * They represent a MULTIPLIER against the baseSpeedMultiplier from preferences.
 * 
 * Example with baseSpeedMultiplier = 0.3:
 *   - speed(1) → actual speed = 1 × 0.3 = 0.3
 *   - speed(3) → actual speed = 3 × 0.3 = 0.9
 */

/**
 * Extracts speed cues from SVG elements whose IDs are in the form:
 *   speed(1.25)
 *   speed(0.8)
 *   speed(2)
 *
 * @param {SVGElement} svgElement
 * @returns {Array<{ position: number, multiplier: number, rawId: string }>}
 */
export function extractSpeedCues(svgElement) {
  if (!svgElement) return [];

  const matches = svgElement.querySelectorAll("[id^='speed('], [data-oscilla*='speed(']");
  const newSpeedMap = [];
  const newRampMap = [];

  matches.forEach((el) => {
    // Marker DSL lives in the id when authored directly, or in data-oscilla
    // after a DSL-editor save migrates id-DSL to a plain uid + data-oscilla.
    let rawId = el.id?.startsWith("speed(") ? el.id : null;
    if (!rawId) {
      const m = (el.getAttribute("data-oscilla") || "").match(/\bspeed\(\s*(?:value\s*:\s*)?-?[0-9.][^)]*\)/);
      if (!m) return;
      rawId = m[0];
    }
    // shorthand speed(3, …) and the keyed form speed(value:3, …) alike
    const match = rawId.match(/^speed\(\s*(?:value\s*:\s*)?(-?[0-9.]+)/);
    if (!match) return;

    // A dur: makes it a RAMP — a trigger cue, not an instant marker. It stays
    // out of the positional map, whose instant apply would beat the ramp and
    // drop the speed the moment the element arrived. (cueDispatcher routes
    // shorthand-with-dur to the trigger list with the same test.)
    // A ramp goes in its own map (below) instead: the region AFTER it runs at
    // its target, and a jump landing there without crossing it must know.
    // getSpeedForPosition reads that map; the crossing watcher never does.
    const isRamp = /\bdur\s*:/.test(rawId);

    const speedValue = parseFloat(match[1]);
    if (!Number.isFinite(speedValue)) return;

    // Absolute X position, in the space window.playheadX is measured in —
    // getSpeedForPosition compares the two.
    // Not `bbox.x + matrix.e` off el.getCTM(): that adds a SCALED translation
    // to an UNSCALED coordinate, and inside a translated <g> the marker fires
    // in the wrong place. See worldCoords.js.
    const x = worldLeftX(el);

    // Optional params: uid (identity) and loop:N (apply at most N times,
    // then go inert — bounds reverse-loops like speed(-1, loop:2))
    const uidMatch = rawId.match(/uid:\s*([^,)\s]+)/);
    const loopMatch = rawId.match(/loop:\s*(\d+)/);

    if (isRamp) { newRampMap.push({ position: x, multiplier: speedValue, rawId, ramp: true }); return; }
    newSpeedMap.push({
      position: x,
      multiplier: speedValue,
      rawId,
      uid: uidMatch ? uidMatch[1] : null,
      loop: loopMatch ? Number(loopMatch[1]) : null,
      applied: 0,
    });
  });

  newSpeedMap.sort((a, b) => a.position - b.position);
  newRampMap.sort((a, b) => a.position - b.position);
  setSpeedRampMap(newRampMap);
  return newSpeedMap;
}

export let speedCueMap = [];
/** Ramp cues by position, at their TARGET speed: for position lookups only, never the crossing watcher. */
export let speedRampMap = [];
export function setSpeedRampMap(newMap) {
  speedRampMap.length = 0;
  speedRampMap.push(...newMap);
}

/**
 * Updates the shared speedCueMap from external code (e.g., scoreSetup.js)
 */
export function setSpeedCueMap(newMap) {
  speedCueMap.length = 0;
  speedCueMap.push(...newMap);
}

/**
 * Returns the CUE speed multiplier for a given X position.
 * This is the raw value from the speed(x) cue, NOT multiplied by base.
 * Traversal changes are ALWAYS instantaneous.
 */
export function getSpeedForPosition(x) {
  if (!speedCueMap.length && !speedRampMap.length) return 1;
  // the last speed cue at or before x, markers and ramps alike -- a ramp at
  // its target, since that is what the region after it runs at
  let multiplier = 1, best = -Infinity;
  for (const cue of speedCueMap) {
    // Exhausted loop:N markers are inert
    if (cue.loop != null && cue.applied >= cue.loop) continue;
    if (x >= cue.position && cue.position >= best) { multiplier = cue.multiplier; best = cue.position; }
  }
  for (const ramp of speedRampMap) {
    if (x >= ramp.position && ramp.position >= best) { multiplier = ramp.multiplier; best = ramp.position; }
  }
  return multiplier;
}


/**
 * ============================================================================
 * SPEED POSITION WATCHER — runs every frame
 * ============================================================================
 * Checks what speed should be at current playheadX and updates if needed.
 * Respects baseSpeedMultiplier from preferences.
 */
let lastWatchX = null; // playhead position at the previous watcher tick

/**
 * Direction-aware crossing watcher. The speed that applies is the value of
 * the LAST speed cue the playhead CROSSED in its direction of travel:
 *
 *  - moving forward, crossing speed(N) left→right applies N
 *  - moving in reverse (negative speed), crossing speed(N) right→left
 *    applies N — so a composer can place speed(1) earlier in the score to
 *    resume forward motion, or pair speed(1)@A with speed(-1)@B to loop
 *    the region between A and B
 *
 * Pure position-zone mapping would re-apply the zone speed one pixel left
 * of a speed(-N) cue and park the playhead jittering on the boundary.
 */
export function checkSpeedForPosition() {
  // Guard clauses
  if (!speedCueMap || speedCueMap.length === 0) return;
  if (!transport.scoreWidth || transport.playheadX == null) return;
  if (window.isSeeking) { lastWatchX = null; return; }  // Don't fight with manual seeking

  const x = transport.playheadX;
  if (lastWatchX == null) { lastWatchX = x; return; }
  const prevX = lastWatchX;
  lastWatchX = x;
  if (x === prevX) return;

  // Find the last cue crossed between prevX and x, in travel direction
  let crossed = null;
  if (x > prevX) {
    for (const cue of speedCueMap) {
      if (cue.position > prevX && cue.position <= x) crossed = cue; // last one wins
      if (cue.position > x) break;
    }
  } else {
    for (let i = speedCueMap.length - 1; i >= 0; i--) {
      const cue = speedCueMap[i];
      if (cue.position < prevX && cue.position >= x) crossed = cue; // last one crossed
      if (cue.position < x) break;
    }
  }
  if (!crossed) return;

  // loop:N — the marker applies at most N times, then goes inert so the
  // playhead passes through (bounded reverse-loops exit this way)
  if (crossed.loop != null) {
    if (crossed.applied >= crossed.loop) return;
    crossed.applied++;
  }

  const cueSpeed = crossed.multiplier;
  const baseMultiplier = window.baseSpeedMultiplier || 1;
  const correctSpeed = cueSpeed * baseMultiplier;

  // NOTE: no dedup on the last cue speed here — a crossing is a one-shot
  // event, and the speed may have been changed by the trigger path
  // (handleSpeedCue fires on the element's screen-edge crossing, slightly
  // before the scalar playhead reaches the marker). Comparing against the
  // ACTUAL current speed below is the only correct guard.
  
  // Skip if already at correct speed (with small tolerance for floating point)
  if (Math.abs(transport.speedMultiplier - correctSpeed) < 0.001) return;

  // Update locally
  setSpeedMultiplier(correctSpeed);
  window.updateSpeedDisplay?.();
  
  // Broadcast to server
  if (window.wsEnabled && window.socket?.readyState === WebSocket.OPEN) {
    window.socket.send(JSON.stringify({
      type: "set_speed_multiplier",
      multiplier: correctSpeed,
      cueSpeed: cueSpeed,
      baseMultiplier: baseMultiplier,
      source: "position_watch"
    }));
  }
}


/**
 * ============================================================================
 * SPEED RAMP ENGINE — LOCAL ONLY
 * ============================================================================
 * Ramps animate speed LOCALLY.
 * The server is updated ONLY ONCE at ramp completion.
 * 
 * NOTE: Ramp values are ACTUAL speeds (already multiplied by base), not cue values.
 */

let activeSpeedRamp = null;

/** Drop a ramp under way, leaving the speed where it is (a project load, a jump out of its box). */
export function cancelSpeedRamp() {
  activeSpeedRamp = null;      // its next frame sees none and ends the loop
}
onReset("project", cancelSpeedRamp);    // an old ramp's box is no place in the new score

export function handleSpeedRamp(start, end, durSec, easing = "linear") {
  if (
    !Number.isFinite(start) ||
    !Number.isFinite(end) ||
    !Number.isFinite(durSec) ||
    durSec <= 0
  ) {
    console.warn("[cueSpeed] Invalid ramp params:", { start, end, durSec });
    return;
  }

  const startTime = performance.now();
  const durationMs = durSec * 1000;

  // Cancel any existing ramp
  activeSpeedRamp = { start, end, startTime, durationMs, easing };

  if (!window.speedRampLoopActive) {
    window.speedRampLoopActive = true;
    requestAnimationFrame(speedRampTick);
  }
}

/**
 * speed(value:N, dur:edge) — the ramp is tied to the element's DRAWN WIDTH,
 * not to a time. Progress is the playhead's position across the bounding
 * box, so the change begins at the left edge, completes exactly at the
 * right edge, and the score shows where the rall lives. A time converted
 * from the width would drift: the ramp itself changes the speed, so the box
 * takes more (or less) time to cross than the entry speed predicts.
 * Scrubbing backwards inside the box walks the ramp back too.
 */
export function handleSpeedRampOverElement(start, end, element, easing = "linear") {
  if (!Number.isFinite(start) || !Number.isFinite(end) || !element) {
    console.warn("[cueSpeed] Invalid edge-ramp params:", { start, end, element });
    return;
  }
  const { centerX, halfW } = worldCenterX(element);
  const x0 = centerX - halfW, x1 = centerX + halfW;
  if (!(x1 > x0)) {
    console.warn("[cueSpeed] dur:edge on an element with no width — instant set instead");
    setSpeedMultiplier(end);
    window.updateSpeedDisplay?.();
    return;
  }
  activeSpeedRamp = { start, end, x0, x1, positional: true, easing };
  if (!window.speedRampLoopActive) {
    window.speedRampLoopActive = true;
    requestAnimationFrame(speedRampTick);
  }
}

// Ramp curves. A distance-linear rall is END-weighted in wall time — the
// fast half of the box flies by and most of the ramp's DURATION sits in its
// slow tail (3→1 spends 37% of the time in the first half, 63% in the
// second). ease:out moves the change earlier to counter that; ease:in
// delays it further; inout rounds both corners.
const RAMP_EASINGS = {
  linear: (t) => t,
  in:     (t) => t * t,
  out:    (t) => 1 - (1 - t) * (1 - t),
  inout:  (t) => t * t * (3 - 2 * t),          // smoothstep
};

function rampEase(name) {
  const key = String(name || "linear").toLowerCase().replace(/^ease-?/, "").replace(/-/g, "");
  return RAMP_EASINGS[key] || RAMP_EASINGS.linear;
}

function speedRampTick(now) {
  if (!activeSpeedRamp) {
    window.speedRampLoopActive = false;
    return;
  }

  const { start, end, startTime, durationMs } = activeSpeedRamp;
  let t;
  if (activeSpeedRamp.positional) {
    const px = transport.playheadX;
    t = px == null ? 0 : (px - activeSpeedRamp.x0) / (activeSpeedRamp.x1 - activeSpeedRamp.x0);
    t = Math.max(0, Math.min(t, 1));
  } else {
    const elapsed = now - startTime;
    t = Math.min(elapsed / durationMs, 1);
  }
  t = rampEase(activeSpeedRamp.easing)(t);

  const value = start + (end - start) * t;

  // LOCAL UPDATE ONLY
  setSpeedMultiplier(value);
  window.updateSpeedDisplay?.();

  if (t < 1) {
    requestAnimationFrame(speedRampTick);
  } else {
    // ---- RAMP COMPLETE ----
    setSpeedMultiplier(end);
    window.updateSpeedDisplay?.();

    // SINGLE authoritative sync commit
    if (window.wsEnabled && window.socket?.readyState === WebSocket.OPEN) {
      window.socket.send(
        JSON.stringify({
          type: "set_speed_multiplier",
          multiplier: end,
          playheadX: transport.playheadX ?? null,
          t: Date.now(),
        })
      );
    }

    activeSpeedRamp = null;
    window.speedRampLoopActive = false;
  }
}

/**
 * ============================================================================
 * INSTANT SPEED CHANGE (non-ramped cue, e.g., from cueSpeed trigger)
 * ============================================================================
 * NOTE: This receives the CUE value and multiplies by baseSpeedMultiplier
 */
export function handleSpeedCue(_uid, cueMultiplier) {
  cueMultiplier = Number(cueMultiplier);
  // Negative values are legal (reverse playback); only 0 is rejected
  if (!Number.isFinite(cueMultiplier) || cueMultiplier === 0) return;

  const baseMultiplier = window.baseSpeedMultiplier || 1;
  const actualSpeed = cueMultiplier * baseMultiplier;

  if (Math.abs(transport.speedMultiplier - actualSpeed) < 0.001) return;

  setSpeedMultiplier(actualSpeed);
  window.updateSpeedDisplay?.();

  if (window.wsEnabled && window.socket?.readyState === WebSocket.OPEN) {
    window.socket.send(
      JSON.stringify({
        type: "set_speed_multiplier",
        multiplier: actualSpeed,
        cueSpeed: cueMultiplier,
        baseMultiplier: baseMultiplier,
        playheadX: transport.playheadX ?? null,
        t: Date.now(),
      })
    );
  }
}


/**
 * Force-update speed based on current position, after the playhead was put
 * somewhere (relocatePlayhead). fromStart: a rewind, which replays the
 * bounded loops; the start of a score is not x 0 (rewindToStart leaves the
 * title page in view), so the place alone cannot say so. broadcast: false
 * when following another client's jump, as that client tells the room.
 */
export function updateSpeedFromPosition({ fromStart = false, broadcast = true } = {}) {
  // A positional ramp holds only inside its box: there a scrub walks it, but
  // landing outside it lets it go, or its frames would pin the speed at its
  // start (before the box) over every marker until the box came round again
  const r = activeSpeedRamp;
  const px = transport.playheadX;
  if (r?.positional && (px == null || px < r.x0 || px > r.x1)) cancelSpeedRamp();

  if (!speedCueMap.length && !speedRampMap.length) return;

  // Re-baseline the crossing watcher so a seek/jump doesn't register as a
  // crossing of every marker in between
  lastWatchX = null;

  // Full rewind resets loop:N counters so bounded loops replay
  if (fromStart || (transport.playheadX ?? 0) <= 1) {
    for (const c of speedCueMap) c.applied = 0;
  }

  const cueSpeed = getSpeedForPosition(transport.playheadX);
  const baseMultiplier = window.baseSpeedMultiplier || 1;
  const correctSpeed = cueSpeed * baseMultiplier;

  setSpeedMultiplier(correctSpeed);
  window.updateSpeedDisplay?.();

  // Broadcast to server
  if (broadcast && window.wsEnabled && window.socket?.readyState === WebSocket.OPEN) {
    window.socket.send(JSON.stringify({
      type: "set_speed_multiplier",
      multiplier: correctSpeed,
      source: "seek_update"
    }));
  }
}


